import { createGlobalState } from '@vueuse/core'
import { ref as dbRef, onValue, goOffline, goOnline } from 'firebase/database'
import dayjs from 'dayjs'
import { rtdb } from '~/firebaseInstance'

// Clock-step guard: Firebase computes `.info/serverTimeOffset` ONCE per socket
// handshake (offset = serverTime - deviceClock at that instant) and never
// re-estimates while the socket stays alive. If the device clock changes AFTER
// the handshake (NTP correcting a wrong-clock boot, wake from sleep), every
// stamp is off by the full jump until a reconnect. The heartbeat below is pure
// local math — zero network; the only traffic is one forced reconnect when a
// real step is detected.
const HEARTBEAT_MS = 60_000
const STEP_THRESHOLD_MS = 30_000
const REHANDSHAKE_COOLDOWN_MS = 60_000
const REFRESH_WAIT_TIMEOUT_MS = 5_000
// A genuine NTP settle needs 1-2 forced reconnects; hitting this cap means the
// clock is chronically stepping (bad RTC, VM, sleep/wake cycling) and another
// reconnect won't fix it — each one costs a full listener re-registration
// (~180 subscriptions in a 90-table shop). Once capped, `stale` may stay
// latched until a natural reconnect clears it, so refresh() pays its bounded
// wait on open/close-shop — accepted for a pathological clock.
const MAX_REHANDSHAKES_PER_SESSION = 5

export const useServerTime = createGlobalState(() => {
  const offsetMs = ref<number | null>(null)
  const ready = ref(false)
  // True between a detected clock step and the next completed handshake — the
  // stored offset may be compensating a device clock that no longer exists.
  const stale = ref(false)
  let started = false
  let lastWall = 0
  let lastPerf = 0
  // performance.now()-based on purpose: the cooldown must survive the very
  // Date.now() jumps we are guarding against.
  let lastRehandshakeAt = 0
  let rehandshakeCount = 0
  let pendingRehandshakeTimer: ReturnType<typeof setTimeout> | null = null

  const requestRehandshake = () => {
    // Offline: leave `stale` latched — the SDK re-anchors the offset on its own
    // reconnect when connectivity returns; the 'online' handler also kicks it.
    if (typeof navigator !== 'undefined' && !navigator.onLine) return
    if (rehandshakeCount >= MAX_REHANDSHAKES_PER_SESSION) return

    const sinceLast = performance.now() - lastRehandshakeAt
    if (lastRehandshakeAt !== 0 && sinceLast < REHANDSHAKE_COOLDOWN_MS) {
      // Coalesce repeated steps into one deferred re-handshake at cooldown
      // expiry — a step during cooldown must not leave `stale` latched forever.
      if (!pendingRehandshakeTimer) {
        pendingRehandshakeTimer = setTimeout(() => {
          pendingRehandshakeTimer = null
          if (stale.value) requestRehandshake()
        }, REHANDSHAKE_COOLDOWN_MS - sinceLast)
      }
      return
    }
    lastRehandshakeAt = performance.now()
    rehandshakeCount++
    try {
      // goOnline resets the SDK's reconnect backoff and connects immediately —
      // the fresh handshake re-anchors `.info/serverTimeOffset`. The SDK
      // re-registers listeners and retries queued writes itself.
      goOffline(rtdb)
      goOnline(rtdb)
    } catch (e) {
      console.warn('[serverTime] forced re-handshake failed', e)
    }
  }

  // Detect device-clock steps by comparing wall-clock elapsed vs monotonic
  // elapsed since the previous check. A stable-but-wrong clock produces zero
  // divergence, so it can never trigger (no refresh loop on mis-set clocks);
  // background-tab throttling stretches both deltas equally (no false positive).
  const checkClockStep = () => {
    const wall = Date.now()
    const perf = performance.now()
    if (lastWall !== 0) {
      const divergence = Math.abs((wall - lastWall) - (perf - lastPerf))
      if (divergence > STEP_THRESHOLD_MS) {
        console.warn(`[serverTime] clock step ~${Math.round(divergence / 1000)}s detected — offset stale, forcing re-handshake`)
        stale.value = true
        requestRehandshake()
      }
    }
    // Reset the baseline every check so the same step isn't re-detected.
    lastWall = wall
    lastPerf = perf
  }

  const init = () => {
    if (started) return
    if (typeof window === 'undefined') return
    started = true

    onValue(dbRef(rtdb, '.info/serverTimeOffset'), (snap) => {
      offsetMs.value = snap.val() ?? 0
      ready.value = true
      stale.value = false
    })

    // Authoritative stale-clear: equal leaf values raise no value event, so the
    // offset listener above will NOT re-fire when the recomputed offset happens
    // to equal the old one. The SDK applies the handshake offset synchronously
    // BEFORE raising connected=true, so clearing here is always safe.
    onValue(dbRef(rtdb, '.info/connected'), (snap) => {
      if (snap.val() === true && stale.value) {
        console.warn('[serverTime] reconnected — offset re-anchored, stale cleared')
        stale.value = false
      }
    })

    // Window-keyed so a dev HMR re-run of this factory can't stack intervals.
    const w = window as any
    if (w.__nlServerTimeHeartbeat) clearInterval(w.__nlServerTimeHeartbeat)
    lastWall = Date.now()
    lastPerf = performance.now()
    w.__nlServerTimeHeartbeat = setInterval(checkClockStep, HEARTBEAT_MS)

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkClockStep()
    })
    window.addEventListener('online', () => {
      checkClockStep()
      if (stale.value) requestRehandshake()
    })
  }

  // `.info/serverTimeOffset` is a synthetic client-side node — it is only ever
  // populated through the `onValue` listener above. Calling `get()` on it forces
  // a server round-trip that the server rejects ("Invalid token in path"), so we
  // resolve from the listener instead of fetching. Also waits out a stale window
  // (bounded — must never hang an offline open/close-shop flow).
  const refresh = () => {
    init()
    checkClockStep()
    if (ready.value && !stale.value) return Promise.resolve(offsetMs.value)
    return new Promise<number | null>((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        stop()
        clearTimeout(timer)
        resolve(offsetMs.value)
      }
      const stop = watch([ready, stale], ([isReady, isStale]) => {
        if (isReady && !isStale) finish()
      })
      const timer = setTimeout(finish, REFRESH_WAIT_TIMEOUT_MS)
    })
  }

  const nowISO = () =>
    dayjs().add(offsetMs.value ?? 0, 'millisecond').toISOString()

  const nowMs = () => Date.now() + (offsetMs.value ?? 0)

  const nowDayjs = () =>
    dayjs().add(offsetMs.value ?? 0, 'millisecond')

  return { init, refresh, nowISO, nowMs, nowDayjs, offsetMs, ready, stale }
})
